<template>
  <UModal
    title="Зарегистрировать школу"
    description="Заполните заявку для регистрации спортивной школы на платформе. После проверки администратором вы получите доступ к личному кабинету школы"
    overlay
    :ui="{
      header: 'p-4 min-h-max',
      content: 'min-w-162.5 max-md:min-w-9/10 max-sm:min-w-full',
      body: 'pt-4',
      title: 'typo-heading-h3 pr-15',
      description: 'typo-paragraph-sm-regular!',
      close: 'border border-neutral-border-default',
      overlay: 'bg-overlay',
    }"
  >
    <template #body>
      <div class="flex items-center mb-6 max-sm:hidden">
        <div
          v-for="(card, index) in infoCards"
          :key="index"
          class="flex items-center h-full w-full grow"
        >
          <UCard
            class="h-31 w-45.5 rounded-[16px]"
            :ui="{
              body: 'p-3 sm:p-3',
            }"
          >
            <UIcon
              class="block w-8 h-8 mx-auto mb-3 text-accent-default"
              :name="card.icon"
            />
            <div
              class="w-full text-[14px]! text-neutral-default text-center font-medium mb-1"
            >
              {{ card.title }}
            </div>

            <p class="text-neutral-light text-xs text-center">
              {{ card.description }}
            </p>
          </UCard>

          <UButton
            v-if="index !== infoCards.length - 1"
            icon="i-local-chevron-right"
            variant="ghost"
            size="xs"
            :ui="{
              leadingIcon: 'text-neutral-muted',
            }"
          />
        </div>
      </div>

      <div class="sm:hidden mb-4">
        <ul class="space-y-1">
          <li
            v-for="(card, index) in infoCards"
            :key="index"
            class="list-disc ml-4"
          >
            <span class="text-sm font-semibold">{{ card.title }}<br /></span>
            <span class="text-xs text-neutral-400">{{ card.description }}</span>
          </li>
        </ul>
      </div>

      <UForm ref="formRef" v-model="form" class="space-y-3 space-x-1">
        <h4 class="text-size-h4 font-semibold mb-3">Основная информация</h4>

        <UInput
          v-model="form.name"
          class="w-full"
          placeholder="Название школы *"
          required
        />

        <div class="flex gap-3">
          <UInput
            v-model="form.email"
            class="w-full"
            type="email"
            placeholder="Ваша почта *"
            required
          />
          <UInput
            v-model="form.phone"
            type="tel"
            class="w-full"
            placeholder="Ваш телефон *"
            required
          />
        </div>

        <UInput
          v-model="form.address"
          class="w-full"
          placeholder="Адрес *"
          required
        />

        <UInput
          v-model="form.region"
          class="w-full"
          placeholder="Регион *"
          required
        />

        <UInput
          v-model="form.licenseNumber"
          class="w-full"
          placeholder="Номер лицензии *"
          required
        />

        <UTextarea
          v-model="form.description"
          class="w-full"
          placeholder="Описание школы *"
          required
          :autoresize="true"
        />
        <p class="text-size-ultrasmall text-neutral-light mb-6">
          Расскажите о вашей школе, её истории, достижениях и особенностях...
        </p>

        <h4 class="text-size-h4 font-semibold mb-3">Документы</h4>
        <p class="mb-3">Для одобрения заявки вам необходимо ниже загрузить:</p>

        <div class="sm:hidden mb-4">
          <ul>
            <li
              v-for="(card, index) in documentsCards"
              :key="index"
              class="list-disc text-xs ml-4"
            >
              {{ card.title }}
            </li>
          </ul>
        </div>
        <div
          class="flex justify-between gap-2.5 mb-3 max-sm:flex-col max-sm:hidden"
        >
          <UCard
            v-for="(card, index) in documentsCards"
            :key="index"
            class="w-full h-26.5 rounded-[16px]"
            :ui="{
              body: 'p-3 sm:p-3 text-center text-content-default text-size-ultrasmall',
            }"
          >
            <UIcon
              :name="card.icon"
              class="block mx-auto text-center text-neutral-light w-6 h-6 mb-2.5"
            />
            {{ card.title }}
          </UCard>
        </div>

        <UFileUpload
          v-model="form.documents"
          class="mb-6"
          icon="i-local-documents-plus"
          description="Файл в формате wav или mp3, до 5 Mб"
          multiple
          layout="list"
          file-icon="i-local-document"
          :ui="{
            base: 'bg-[#8691A30D]',
            description: 'text-size-ultrasmall text-content-default-secondary',
          }"
        >
          <template #label>
            <div class="text-size-normal font-semibold mb-1.5">
              Перетащите файл или
              <span class="text-accent-default">выберите на компьютере</span>
            </div>
          </template>
        </UFileUpload>

        <UCheckbox
          v-model="form.agree"
          required
          :ui="{
            wrapper: 'font-lato! text-xs',
          }"
        >
          <template #label>
            Я ознакомлен(-а) с <a
              href="/documents/personal-data"
              target="_blank"
              rel="noopener noreferrer"
              class="text-link"
            >
              Политикой обработки персональных данных
            </a>
            и даю своё
            <a
              href="/documents/personal-data"
              target="_blank"
              rel="noopener noreferrer"
              class="text-link"
            >
              согласие на обработку персональных данных</a
            >.
          </template>
        </UCheckbox>
      </UForm>
    </template>

    <template #footer>
      <UButton
        class="w-full"
        :disabled="isSubmitDisabled"
        variant="solid"
        @click="submit"
      >
        Отправить заявку
      </UButton>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
defineOptions({
  name: 'SchoolRegisterModal',
})

const formRef = useTemplateRef('formRef')

const infoCards = [
  {
    icon: 'i-local-file-edit',
    title: 'Подайте заявку',
    description: 'Заполните форму с информацией о школе',
  },
  {
    icon: 'i-local-file-search',
    title: 'Модерация',
    description: 'Администратор проверит ваши данные',
  },
  {
    icon: 'i-local-user-check',
    title: 'Начните работу',
    description: 'Получите доступ к личному кабинету',
  },
]

const documentsCards = [
  {
    icon: 'i-local-file-award',
    title: 'Копию лицензии на образовательную деятельность',
  },
  {
    icon: 'i-local-file-user',
    title: 'Основной учредительный документ',
  },
  {
    icon: 'i-local-file-box',
    title: 'Свидетельство о регистрации',
  },
  {
    icon: 'i-local-file-image',
    title: 'Фотографии помещений и оборудования',
  },
]

const form = reactive({
  name: '',
  description: '',
  email: '',
  phone: '',
  address: '',
  region: '',
  licenseNumber: '',
  documents: [],
  agree: false,
})

const isSubmitDisabled = computed(() =>
  Object.values(form).some((value) => {
    if (Array.isArray(value) && value?.length === 0) {
      return true
    }

    return value === ''
  }),
)

const submit = () => {
  formRef.value?.submit()
}
</script>

<style></style>
